import { useSyncExternalStore } from 'react';

// #928 -- one shared, browser-stored value per preference. Every component reading a preference
// sees the same value at once (a theme button on the Monograph and the account menu stay in step),
// and another tab changing it is picked up through the `storage` event. Browser-only for now;
// syncing to the account is a later follow-up (owner, 2026-10-11).

export interface StoredPreference<T> {
  readonly get: () => T;
  readonly set: (next: T) => void;
  readonly subscribe: (listener: () => void) => () => void;
}

interface PreferenceOptions<T> {
  /** The localStorage key -- also how a change from another tab is recognised. */
  readonly key: string;
  /** Reads the stored value; must never throw (fall back to the default). */
  readonly read: () => T;
  /** Stores a value; best-effort, must never throw. */
  readonly write: (value: T) => void;
}

export function createPreference<T>({ key, read, write }: PreferenceOptions<T>): StoredPreference<T> {
  let current: T | undefined;
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const onStorage = (event: StorageEvent) => {
    if (event.key !== key && event.key !== null) return;
    current = read();
    notify();
  };

  return {
    get: () => {
      if (current === undefined) current = read();
      return current;
    },
    set: (next) => {
      write(next);
      current = next;
      notify();
    },
    subscribe: (listener) => {
      if (listeners.size === 0 && typeof window !== 'undefined') window.addEventListener('storage', onStorage);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
      };
    }
  };
}

/** The preference's current value; re-renders when it changes anywhere. */
export function usePreference<T>(preference: StoredPreference<T>): T {
  return useSyncExternalStore(preference.subscribe, preference.get, preference.get);
}

/** A string preference limited to known values: anything else reads as the default. */
export function createChoicePreference<T extends string>(
  key: string,
  choices: readonly T[],
  fallback: T
): StoredPreference<T> {
  return createPreference<T>({
    key,
    read: () => {
      try {
        const stored = window.localStorage.getItem(key);
        return stored !== null && (choices as readonly string[]).includes(stored) ? (stored as T) : fallback;
      } catch {
        return fallback;
      }
    },
    write: (value) => {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Best-effort: private browsing or a full quota keeps the in-memory value for this visit.
      }
    }
  });
}

/** An on/off preference. */
export function createSwitchPreference(key: string, fallback: boolean): StoredPreference<boolean> {
  const stored = createChoicePreference<'on' | 'off'>(key, ['on', 'off'], fallback ? 'on' : 'off');
  return {
    get: () => stored.get() === 'on',
    set: (next) => stored.set(next ? 'on' : 'off'),
    subscribe: stored.subscribe
  };
}
